import { cloneDeep } from 'lodash';
import { AskResponse, Citation } from '../api';

export type ParsedAnswer = {
  citations: Citation[];
  markdownFormatText: string;
  generated_chart: string | null;
} | null;

/**
 * Enumerate citations by filepath to assign part_index
 * Multiple citations from the same file get numbered as Part 1, Part 2, etc.
 */
export const enumerateCitations = (citations: Citation[]): Citation[] => {
  const filepathMap = new Map<string, number>();
  
  for (const citation of citations) {
    const { filepath } = citation;
    if (!filepath) continue;
    
    let part_i = 1;
    if (filepathMap.has(filepath)) {
      part_i = filepathMap.get(filepath)! + 1;
    }
    filepathMap.set(filepath, part_i);
    citation.part_index = part_i;
  }
  
  return citations;
};

/**
 * Parse answer text to extract and format citations
 * Replaces [doc1], [doc2] markers with superscript numbers ^1^, ^2^
 * De-duplicates citations and enumerates them by filepath
 */
export function parseAnswer(answer: AskResponse): ParsedAnswer {
  if (typeof answer.answer !== 'string') return null;
  
  let answerText = answer.answer;
  const citationLinks = answerText.match(/\[(doc\d\d?\d?)]/g);

  const lengthDocN = '[doc'.length;

  let filteredCitations: Citation[] = [];
  let citationReindex = 0;

  citationLinks?.forEach(link => {
    // Extract the citation index from [doc1], [doc2], etc.
    const citationIndex = link.slice(lengthDocN, link.length - 1);
    const citation = cloneDeep(answer.citations[Number(citationIndex) - 1]) as Citation;
    
    // De-duplicate citations and replace with superscript numbers
    if (!filteredCitations.find(c => c.id === citationIndex) && citation) {
      answerText = answerText.replaceAll(link, ` ^${++citationReindex}^ `);
      citation.id = citationIndex; // original doc index to de-dupe
      citation.reindex_id = citationReindex.toString(); // reindex from 1 for display
      filteredCitations.push(citation);
    }
  });

  filteredCitations = enumerateCitations(filteredCitations);

  return {
    citations: filteredCitations,
    markdownFormatText: answerText,
    generated_chart: answer.generated_chart,
  };
}

